import { useMemo } from 'react';
import { StyledTableRow, StyledTableCell } from '@/components/ui/styled-table';
import type { PendingGroupProposalWithSignatures } from '@/types/group-proposal';
import { ApprovalCountsDisplay } from '@/components/shared/approval-counts-display';
import { VoteStatusBadge } from '@/components/shared/vote-status-badge';
import { ChangeBadge } from '@/components/ui/change-badge';
import { getChangeBadges } from '@/types/group-changes';
import { useAuthClient } from '@/lib/authClient';
import { getSignatureCountsAndUserVote } from '@/utils/SignatureCountsHelpers';

interface GroupProposalRowProps {
  proposal: PendingGroupProposalWithSignatures;
  onRowClick: (proposal: PendingGroupProposalWithSignatures) => void;
}

export function GroupProposalRow({
  proposal,
  onRowClick,
}: GroupProposalRowProps) {
  const { proposal: proposalData } = proposal;
  const { user } = useAuthClient();
  const changeBadges = getChangeBadges(proposalData);

  const { numApprovals, numRejections, userVote } = useMemo(
    () =>
      getSignatureCountsAndUserVote({
        approvals: proposal.approvals,
        rejections: proposal.rejections,
        userId: user?.id,
        requiredNetworkIds: proposal.requiredNetworkIds,
      }),
    [
      proposal.approvals,
      proposal.rejections,
      proposal.requiredNetworkIds,
      user?.id,
    ]
  );

  return (
    <StyledTableRow
      className="cursor-pointer"
      onClick={() => onRowClick(proposal)}
    >
      <StyledTableCell className="font-semibold text-base">
        {proposalData.action === 'CREATE'
          ? (proposalData.groupName ?? '(Unnamed)')
          : (proposalData.group?.name ?? '(Unnamed)')}
      </StyledTableCell>
      <StyledTableCell className="font-normal">
        <div className="flex flex-wrap gap-1.5">
          {changeBadges.map((badge, badgeIndex) => (
            <ChangeBadge
              key={badgeIndex}
              changeType={badge.changeType}
              text={badge.text}
            />
          ))}
        </div>
      </StyledTableCell>
      <StyledTableCell>
        <ApprovalCountsDisplay
          approvals={numApprovals}
          rejections={numRejections}
          threshold={proposal.requiredApprovals}
        />
      </StyledTableCell>
      <StyledTableCell>
        <VoteStatusBadge userVote={userVote} />
      </StyledTableCell>
    </StyledTableRow>
  );
}
